import React, { useRef, useEffect } from 'react';
import { useGame } from '../../context/GameContext';
import './TurnManagement.css';

const Guessing = () => {
  const { 
    gameState, 
    isMyTurn, 
    activePlayerName,
    wordDescription,
    dictionary,
    guessInput,
    setGuessInput,
    guesses,
    validationError,
    handleSubmitGuess
  } = useGame();

  const guessInputRef = useRef(null);

  // Focus the guess input when the component mounts and when isMyTurn changes
  useEffect(() => {
    if (!isMyTurn && guessInputRef.current && gameState === 'guessing') {
      guessInputRef.current.focus();
    }
  }, [isMyTurn, gameState]);

  // Only show this component when game state is guessing
  if (gameState !== 'guessing') {
    return null;
  }

  return (
    <div className="turn-management-section">
      <div className="guessing-section">
        <h3>{isMyTurn ? 'Your Description' : `${typeof activePlayerName === 'string' ? activePlayerName : ''}'s Description`}</h3>
        <div className="word-description-display">
          <div className="description-word-cards">
            {wordDescription.split(' ').filter(word => word.trim()).map((word, index) => {
              // Find if this is a Portuguese or Gibberers word
              let isPortuguese = false;
              let translatedWord = '';
              
              if (Array.isArray(dictionary)) {
                const entry = dictionary.find(e => 
                  e.word.toLowerCase() === word.toLowerCase() || 
                  e.spelling.toLowerCase() === word.toLowerCase()
                );
                
                if (entry) {
                  isPortuguese = entry.word.toLowerCase() === word.toLowerCase();
                  translatedWord = isPortuguese ? entry.spelling : entry.word;
                }
              } else {
                // Handle old dictionary format
                const portugueseMatch = Object.keys(dictionary).find(
                  key => key.toLowerCase() === word.toLowerCase()
                );
                
                if (portugueseMatch) {
                  isPortuguese = true;
                  translatedWord = dictionary[portugueseMatch].newWord;
                } else {
                  // Check if it's a Gibberers word
                  for (const [key, value] of Object.entries(dictionary)) {
                    if (value.newWord && value.newWord.toLowerCase() === word.toLowerCase()) {
                      isPortuguese = false;
                      translatedWord = key;
                      break;
                    }
                  }
                }
              }
              
              return (
                <div key={index} className={`description-word-card ${isPortuguese ? 'portuguese-word' : 'gibberers-word'}`}>
                  <span className="word-text">{word}</span>
                  {translatedWord && (
                    <span className="word-translation" title={`Translation: ${translatedWord}`}>
                      ({translatedWord})
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {!isMyTurn && (
          <div className="guess-input-container">
            <input
              ref={guessInputRef}
              type="text"
              value={guessInput}
              onChange={(e) => setGuessInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSubmitGuess()}
              placeholder="Enter your guess..."
              className="guess-input"
            />
            {validationError && <p className="validation-error">{validationError}</p>}
            <button 
              className="submit-guess-btn"
              onClick={handleSubmitGuess}
              disabled={!guessInput.trim() || isMyTurn}
            >
              Submit Guess
            </button>
          </div>
        )}
        
        {/* Guesses list */}
        {guesses.length > 0 && (
          <div className="guesses-list">
            <h4>Guesses:</h4>
            <ul>
              {guesses.map((guess, index) => (
                <li 
                  key={index} 
                  className={`guess-item ${guess.isCorrect ? 'correct-guess' : 'incorrect-guess'}`}
                >
                  <span className="guesser-name">{typeof guess.playerName === 'string' ? guess.playerName : ''}:</span> 
                  <span className="guess-text">{guess.guess}</span>
                  {guess.isCorrect && <span className="correct-indicator">✓ Correct!</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default Guessing; 